import React from 'react';
import IframeWidget from "../../../../utils/IframeWidget";
import {Modal} from "react-bootstrap";
import {ReactComponent as LeftIco} from "../../../../../images/icons/arrow-left.svg";
import MenuCategoryProduct from "./MenuCategoryProduct";
import {ConfigApi, MenuProductViewType, OrderType} from "../../../../services/Api/types";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import CategoryHelper from "../../../../services/Menu/services/Category/CategoryHelper";
import StoreUtils from "../../../../services/Store/StoreUtils";
import AppConfig from "../../../../services/AppConfig";
import useTemporarilyClosedInfo from "../../../../hooks/useTemporarilyClosedInfo";
import {useLocation, useNavigate} from "react-router-dom";
import {usePrefix} from "../../../../services/Prefix";
import {createKeyDownHandler} from "../../../../utils/keyDownHanlder";
import {useTranslation} from "react-i18next";
import ResponsiveComponent from "../../../../components/Common/ResponsiveComponent";
import MenuCategoryAvailabilityHeading from "./MenuCategoryAvailabilityHeading";
import {getParsedSessionOrderType} from "../../../../utils/order";
import AvailableHelper from "../../../../services/Menu/services/Availability/AvailableHelper";

interface Props {
    category: Record<string, any>;
    show: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
    availabilities: any[];
    session: any;
    promotions: any[];
    config: ConfigApi | undefined;
    locale: string | undefined;
    menus: any[];
}

const MenuCategoryItemsModal = ({
                                    category,
                                    show,
                                    setShowModal,
                                    session,
                                    promotions,
                                    config,
                                    availabilities,
                                    locale,
                                    menus,
                                }: Props) => {
    const sortedCategoryItems = CategoryHelper.getSortedCategoryItems(category);
    const parsedOrderType = getParsedSessionOrderType(session.order_type as OrderType);
    const menusWithCategoryForCurrentOrderType = menus.filter((menu: any) => menu[parsedOrderType.toLowerCase()] === true);
    const menuAvailabilitiesForCurrentType = AvailableHelper.getAvailabilities(availabilities, menusWithCategoryForCurrentOrderType.map((menu: any) => menu.availability_id));
    const isCategoryAvailableByHoursForCurrentType = HoursHelper.isOpen(menuAvailabilitiesForCurrentType.flatMap((availability: any) => availability.hours), DateService.emptyOrNow(session.pickup_at));
    const isCategoryAvailableByAvailabilityDates = menuAvailabilitiesForCurrentType.some((availability: any) => AvailableHelper.isAvailabile(availability, DateService.emptyOrNow(session.pickup_at)));
    const isCategoryAvailable = isCategoryAvailableByHoursForCurrentType && isCategoryAvailableByAvailabilityDates && menusWithCategoryForCurrentOrderType.length > 0;
    const availableCategoryItems = CategoryHelper.getAvailableCategoryItems(availabilities, session.pickup_at, sortedCategoryItems);
    const isFinallyAvailable = isCategoryAvailable ? isCategoryAvailable && !!availableCategoryItems.length : isCategoryAvailable;
    const {handleShowTemporarilyClosedInfo, drawTemporarilyClosedInfo} = useTemporarilyClosedInfo();
    const navigate = useNavigate();
    const prefix = usePrefix();
    const location = useLocation();
    const {t} = useTranslation();
    const orderType = session.order_type as OrderType;

    const onHide = () => {
        setShowModal(false);

        if (location.state && location.state?.back) {
            if (IframeWidget.isInit()) {
                navigate(StoreUtils.isHomepageMenu(config) ? `/` : '/menu');
            } else {
                navigate(-1);
            }
        } else {
            navigate(AppConfig.getLinkWithPrefix(StoreUtils.isHomepageMenu(config) ? `/` : '/menu', prefix), {
                replace: true,
                state: {action: 'stay'}
            });
        }
    }

    const handleAddItem = (id: number) => {
        if (StoreUtils.isPaused(config)) {
            handleShowTemporarilyClosedInfo();
            return;
        }

        let back = true;
        if (!!config?.old_settings?.homepage_full) {
            back = false;
        }

        navigate(AppConfig.getLinkWithPrefix(`/menu/add/${id}`, prefix), {state: {back, categoryId: category.id}});
    }

    const handleOnGoBackKeyDown = createKeyDownHandler<HTMLDivElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: () => onHide()
    });

    const renderHeader = (smallDevices?: boolean) => {
        return (
            <Modal.Header className={"modal-header-back"} closeButton={!smallDevices}>
                {smallDevices && (
                    <div tabIndex={0} onKeyDown={handleOnGoBackKeyDown} className="back-icon text-muted"
                         onClick={onHide}>
                        <LeftIco className="icon" aria-label={t("common.action.close")}/>
                    </div>
                )}
                <Modal.Title id="category_modal_title">
                    {category.name}
                </Modal.Title>
            </Modal.Header>
        )
    }

    return (
        <>
            {drawTemporarilyClosedInfo()}
            <Modal dialogClassName="modal-full menu-category-items-modal-dialog" show={show} scrollable
                   aria-labelledby="category_modal_title"
                   onHide={() => IframeWidget.onHide(onHide)} onShow={() => IframeWidget.onShow()}>
                <ResponsiveComponent small={renderHeader(true)} large={renderHeader(false)} breakpoint={768}/>
                <Modal.Body>
                    {!isFinallyAvailable && (
                        <div className="mb-4">
                            <MenuCategoryAvailabilityHeading pickupDate={session.pickup_at}
                                                             category={category} orderType={orderType}
                                                             items={sortedCategoryItems}
                                                             locale={locale}
                                                             config={config}
                                                             availabilities={availabilities}
                                                             menus={menus}/>
                        </div>
                    )}
                    <div
                        className={`menu-items menu-items-horizontal-list ${!isFinallyAvailable ? 'menu-items-not-available' : ''}`}>
                        {sortedCategoryItems.map((item: any) => (
                            <MenuCategoryProduct viewType={MenuProductViewType.HORIZONTAL}
                                                 product={item}
                                                 key={item.id}
                                                 category={category}
                                                 forceNotAvailable={!isFinallyAvailable}
                                                 promotions={promotions}
                                                 addItem={handleAddItem}
                            />
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default MenuCategoryItemsModal;